<template>
  <div>
    <ContentWrap title="转播商品">
      <el-alert title="价格以元填写，最低0.01元。源主播用户ID必须与直播间主播一致；购买后使用源直播画面，源结束则停止转播。" type="info" :closable="false" class="mb8" />
      <el-row :gutter="10" class="mb8">
        <el-col :span="1.5">
          <el-button type="primary" plain @click="handleAdd" v-hasPermi="['live:relay:create']">
            <Icon icon="ep:plus" />新增
          </el-button>
        </el-col>
      </el-row>
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="list" border>
        <el-table-column label="编号" prop="id" width="80" />
        <el-table-column label="直播ID" prop="liveId" min-width="100" />
        <el-table-column label="源主播用户ID" prop="sellerUserId" min-width="120" />
        <el-table-column label="标题" prop="title" min-width="160" />
        <el-table-column label="价格（元）" width="120"><template #default="{ row }">{{ (Number(row.price || 0) / 100).toFixed(2) }}</template></el-table-column>
        <el-table-column label="状态" prop="status" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status === 0 ? 'success' : 'info'">{{
              scope.row.status === 0 ? '在售' : '下架'
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          prop="createTime"
          min-width="180"
          :formatter="dateFormatter"
        />
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button
              link
              type="primary"
              @click="handleEdit(scope.row)"
              v-hasPermi="['live:relay:update']"
              >修改</el-button
            >
            <el-button
              link
              type="danger"
              @click="handleDelete(scope.row)"
              v-hasPermi="['live:relay:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>

    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="560px" append-to-body>
      <el-form
        ref="formRef"
        :model="form"
        :rules="withLiveInputLimits(rules, liveInputLimits)"
        label-width="120px"
      >
        <el-form-item label="直播ID" prop="liveId">
          <LiveRoomIdSelect
            v-model="form.liveId"
            select-class="!w-full"
            @change="onLiveChange"
          />
        </el-form-item>
        <el-form-item label="源主播用户ID" prop="sellerUserId">
          <LiveAnchorUserSelect
            ref="anchorSelectRef"
            v-model="form.sellerUserId"
            select-class="!w-full"
          />
          <div class="text-12px text-[var(--el-text-color-secondary)] mt-1"
            >须与所选直播间主播一致；选直播间后会自动带出</div
          >
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input
            show-word-limit
            :maxlength="128"
            v-model="form.title"
            placeholder="请输入标题"
          />
        </el-form-item>
        <el-form-item label="价格（元）" prop="price">
          <el-input-number
            :max="21474836.47"
            v-model="form.price"
            :min="0.01"
            :precision="2"
            controls-position="right"
            class="!w-full"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="0">在售</el-radio>
            <el-radio :label="1">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { withLiveInputLimits } from '@/utils/liveInputLimits'
const liveInputLimits = { title: 128 }

import { dateFormatter } from '@/utils/formatTime'
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { createRelay, updateRelay, deleteRelay, pageRelay } from '@/api/live/relay'
import type { LiveRoomVO } from '@/api/live/room'
import LiveRoomIdSelect from '@/views/live/components/LiveRoomIdSelect.vue'
import LiveAnchorUserSelect from '@/views/live/components/LiveAnchorUserSelect.vue'

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref<any>(null)
const anchorSelectRef = ref<InstanceType<typeof LiveAnchorUserSelect>>()

const queryParams = reactive({ pageNo: 1, pageSize: 10 })

const form = reactive({
  id: undefined as number | undefined,
  liveId: undefined as number | undefined,
  sellerUserId: undefined as number | undefined,
  title: '',
  price: 0.01,
  status: 0
})

const rules = {
  liveId: [{ required: true, message: '请选择源直播间', trigger: 'change' }],
  sellerUserId: [{ required: true, message: '请选择源直播间的主播用户', trigger: 'change' }],
  price: [{ required: true, message: '请输入付费转播价格', trigger: 'change' }],
  title: [{ required: true, message: '标题不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
}

const onLiveChange = async (liveId?: number, room?: LiveRoomVO) => {
  if (!liveId) return
  if (room?.anchorUserId) {
    form.sellerUserId = room.anchorUserId
    await anchorSelectRef.value?.ensureOption(room.anchorUserId)
    formRef.value?.validateField?.('sellerUserId')
  }
  if (!form.title && room?.title) {
    form.title = room.title
  }
}

const getList = async () => {
  loading.value = true
  try {
    const data: any = await pageRelay(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.id = undefined
  form.liveId = undefined
  form.sellerUserId = undefined
  form.title = ''
  form.price = 0.01
  form.status = 0
}

const handleAdd = () => {
  resetForm()
  dialogTitle.value = '新增转播商品'
  dialogVisible.value = true
}

const handleEdit = (row: any) => {
  resetForm()
  Object.assign(form, row, { price: Number(row.price) / 100 })
  dialogTitle.value = '修改转播商品'
  dialogVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  if (!Number.isFinite(form.price) || form.price <= 0) {
    ElMessage.warning('付费转播价格必须大于0')
    return
  }
  const data = { ...form, price: Math.round(form.price * 100) }
  if (form.id) {
    await updateRelay(data)
    ElMessage.success('修改成功')
  } else {
    await createRelay(data)
    ElMessage.success('新增成功')
  }
  dialogVisible.value = false
  getList()
}

const handleDelete = (row: any) => {
  ElMessageBox.confirm('确认删除该转播商品吗？', '提示', { type: 'warning' }).then(async () => {
    await deleteRelay(row.id)
    ElMessage.success('删除成功')
    getList()
  })
}

onMounted(() => {
  getList()
})
</script>
